import { hostApiClient } from '../hostApiClient';
import { tutorApiClient } from '../tutorApiClient';
import { OrganizationType, StripeConnectExpressAccount } from '../models/Organization';

export type StripeConnectVariant = 'host' | 'tutor';

export interface StripeConnectClient {
  getStripeConnectExpressAccount: () => Promise<StripeConnectExpressAccount>;
  createStripeConnectExpressAccount: () => Promise<StripeConnectExpressAccount>;
  resyncStripeConnectExpressAccount: () => Promise<StripeConnectExpressAccount>;
  disconnectStripeConnectExpressAccount: () => Promise<void>;
}

export function getStripeConnectClient(variant: StripeConnectVariant): StripeConnectClient {
  if (variant === 'tutor') {
    // Deep-link / legacy tutor route; prefer host variant. Calls still hit /v1/host/stripe without org header.
    return {
      getStripeConnectExpressAccount: () => tutorApiClient.getStripeConnectExpressAccount(),
      createStripeConnectExpressAccount: () => tutorApiClient.createStripeConnectExpressAccount(),
      resyncStripeConnectExpressAccount: () => tutorApiClient.resyncStripeConnectExpressAccount(),
      disconnectStripeConnectExpressAccount: () => tutorApiClient.disconnectStripeConnectExpressAccount(),
    };
  }

  return {
    getStripeConnectExpressAccount: () => hostApiClient.getStripeConnectExpressAccount(),
    createStripeConnectExpressAccount: () => hostApiClient.createStripeConnectExpressAccount(),
    resyncStripeConnectExpressAccount: () => hostApiClient.resyncStripeConnectExpressAccount(),
    disconnectStripeConnectExpressAccount: () => hostApiClient.disconnectConnectExpress(),
  };
}

export async function resolveOrganizationType(variant: StripeConnectVariant): Promise<OrganizationType | undefined> {
  if (variant === 'tutor') {
    const profile = await tutorApiClient.getTutorProfileSettings();
    return profile.organizationType;
  }

  const organization = await hostApiClient.getOrganization();
  return organization.organizationType;
}

/** Prefer tutor listing when the user is a tutor (covers individual-tutor orgs with HOST role). */
export async function resolveUserOrganizationType(userRoles: string[]): Promise<OrganizationType | undefined> {
  const [tutorType, hostType] = await Promise.all([
    userRoles.includes('TUTOR')
      ? tutorApiClient.getTutorProfileSettings().then((profile) => profile.organizationType).catch(() => undefined)
      : Promise.resolve(undefined),
    userRoles.includes('HOST')
      ? hostApiClient.getOrganization().then((organization) => organization.organizationType).catch(() => undefined)
      : Promise.resolve(undefined),
  ]);

  if (
    tutorType === OrganizationType.INDIVIDUAL_TUTOR ||
    hostType === OrganizationType.INDIVIDUAL_TUTOR
  ) {
    return OrganizationType.INDIVIDUAL_TUTOR;
  }

  return tutorType ?? hostType;
}

/**
 * Stripe Connect is managed from host integrations via `/v1/host/stripe/*`.
 * `INDIVIDUAL_TUTOR` is an organization type (with HOST role), not a separate Stripe flow.
 * The tutor route is retained for deep links and directs users to the host page.
 */
export function isStripeConnectAllowed(
  variant: StripeConnectVariant,
  organizationType: OrganizationType | undefined,
): boolean {
  if (!organizationType) {
    return false;
  }

  return variant === 'host';
}
